// src/pages/Animate/utils/calculateSpriteSheet.ts

// A placeholder for a frame dimension object. 
// In a real scenario, this would likely come from image processing 
// or be a configurable value (e.g., 512x512).
export interface FrameDimensions {
    width: number;
    height: number;
}

export interface SpriteSheetCalculation {
    /** The number of rows for the sprite sheet grid. */
    rows: number;
    /** The number of columns for the sprite sheet grid. */
    cols: number;
    /** The total width of the resulting sprite sheet image in pixels. */
    sheetWidth: number;
    /** The total height of the resulting sprite sheet image in pixels. */
    sheetHeight: number;
    /** The number of empty cells at the end (e.g., if 22 frames in a 5x5 grid, gaps = 3). */
    gapFrames: number;
}

/**
 * Calculates the optimal dimensions and grid layout for a sprite sheet.
 * It aims to minimize the number of rows/columns while keeping them as close
 * to each other as possible (e.g., closest to a square aspect ratio)
 * given the total number of frames.
 * * @param startFrame The index of the first frame (inclusive) to include in the sprite sheet.
 * * @param endFrame The index of the last frame (inclusive) to include in the sprite sheet.
 * @param frameSize The pixel dimensions of a single frame (e.g., 512x512).
 * @returns An object containing the calculated rows, columns, and total pixel dimensions.
 */
export const calculateSpriteSheet = (
    startFrame: number,
    endFrame: number,
    frameSize: FrameDimensions
): SpriteSheetCalculation => {
    
    // Calculate the number of frames to be included in the export (the range is inclusive)
    const framesInExport = Math.max(0, endFrame - startFrame + 1);

    if (framesInExport <= 0) {
        return {
            rows: 0,
            cols: 0,
            sheetWidth: 0,
            sheetHeight: 0,
            gapFrames: 0,
        };
    }
    
    const totalFrames = framesInExport;

    // 1. Calculate the ideal number of columns (cols)
    // We want the grid to be as close to a square as possible.
    // The columns should be ceil(sqrt(totalFrames)).
    const cols = Math.ceil(Math.sqrt(totalFrames));

    // 2. Calculate the number of rows (rows)
    // rows = ceil(totalFrames / cols)
    const rows = Math.ceil(totalFrames / cols);

    // 3. Calculate the total pixel dimensions
    const sheetWidth = cols * frameSize.width;
    const sheetHeight = rows * frameSize.height;
    
    // 4. Calculate the number of gap frames
    const totalCells = rows * cols;
    const gapFrames = totalCells - totalFrames;

    return {
        rows,
        cols,
        sheetWidth,
        sheetHeight,
        gapFrames,
    };
};